import {Button} from "primereact/button";
import {Dialog} from "primereact/dialog";
import {type ReactNode, useEffect} from "react";

import {lifeCycleStateLabelKey} from "@reva/shared/lifecycle-state";

import type {SearchValuationItem} from "../api/payload-types/search-valuation";
import {ColorDotLabel} from "../app/start/startPageWidgets";
import {useRevaTranslation} from "../hooks/languageHooks";
import {useRevaSelector} from "../redux/revaHooks";
import {selectDarkTheme} from "../redux/themeSlice";
import {getLifeCycleStateColors} from "../util/chartUtils";
import {formatDateAndTime} from "../util/dateUtil";
import {EvaPlusOrderLink} from "./EvaPlusOrderLink";

interface AssignValuationDialogProps {
    /** Zu übernehmende Bewertung; der Dialog ist sichtbar, sobald ein Wert gesetzt ist. */
    valuation: SearchValuationItem | null;
    /** Wird bei Bestätigung (Button oder Enter) ausgeführt. */
    onConfirm: () => void;
    /** Wird beim Schließen ohne Übernahme (Abbrechen oder Esc) ausgeführt. */
    onCancel: () => void;
    /** i18n-Key der Dialog-Überschrift (Default: "Auftrag aus dem Pool übernehmen?"). */
    titleKey?: string;
    /** i18n-Key des Bestätigungs-Buttons (Default: "Mir zuweisen"). */
    confirmLabelKey?: string;
    /** Bestätigen sperren (z.B. solange ein Pflicht-Kommentar leer ist); blockiert Button UND Enter. */
    confirmDisabled?: boolean;
    /** Zusätzlicher Inhalt unter der Auftrags-Zusammenfassung (z.B. Pflicht-Kommentar + Owner-Auswahl bei der Korrektur). */
    children?: ReactNode;
}

/** Eine Zeile der kompakten Auftrags-Zusammenfassung (Label links, Wert rechts). */
const SummaryRow = ({label, children}: {label: string; children: ReactNode}) => (
    <>
        <span className="text-surface-500 dark:text-surface-400 whitespace-nowrap">{label}</span>
        <span>{children}</span>
    </>
);

/**
 * Bestätigungs-Dialog für Zeilen-Aktionen der Startseiten-Tabs ("Mir zuweisen" im Auftragspool,
 * "Zur Korrektur öffnen" im Korrekturen-Tab, "In den Pool legen" bei den eigenen Bewertungen):
 * fasst den Auftrag kompakt zusammen (Adresse,
 * Order-Nummer, Status, wann/von wem erstellt und zuletzt bearbeitet), damit man nicht
 * versehentlich den falschen Auftrag anfasst. Überschrift und Bestätigungs-Button sind per
 * i18n-Key parametrisierbar. Enter bestätigt, Esc bricht ab.
 */
export function AssignValuationDialog({
    valuation,
    onConfirm,
    onCancel,
    titleKey = "start.valuations.assignDialog.title",
    confirmLabelKey = "start.valuations.assignToMe",
    confirmDisabled = false,
    children,
}: AssignValuationDialogProps) {
    const {t, language} = useRevaTranslation();
    const isDarkTheme = useRevaSelector(selectDarkTheme);
    // Farben der Lifecycle-Stati wie in der Status-Spalte der Bewertungslisten (siehe getLifeCycleStateColors).
    const lifeCycleColors = getLifeCycleStateColors(isDarkTheme);

    // Enter bestätigt, solange der Dialog offen ist (Esc schließt über das closeOnEscape des Dialogs).
    useEffect(() => {
        if (!valuation) return;
        const onKeyDown = (e: KeyboardEvent) => {
            if (e.key === "Enter" && !confirmDisabled) {
                e.preventDefault();
                onConfirm();
            }
        };
        document.addEventListener("keydown", onKeyDown);
        return () => document.removeEventListener("keydown", onKeyDown);
    }, [valuation, onConfirm, confirmDisabled]);

    if (!valuation) return null;

    const externalOrders = valuation.orders.filter((order) => order.orderType !== "Reva");

    const dateByUser = (date: string, user: string | null | undefined) =>
        t("start.valuations.assignDialog.dateByUser", {
            date: formatDateAndTime(date, language),
            user: user ?? "?",
        });

    const footer = (
        <div>
            <Button label={t("start.valuations.assignDialog.cancel")} className="p-button-text" onClick={onCancel} />
            <Button
                label={t(confirmLabelKey)}
                className="ml-2"
                autoFocus
                disabled={confirmDisabled}
                onClick={onConfirm}
            />
        </div>
    );

    return (
        <Dialog
            visible
            onHide={onCancel}
            footer={footer}
            showCloseIcon={false}
            header={<span className="font-semibold">{t(titleKey)}</span>}
        >
            <div className="grid grid-cols-[auto_1fr] gap-x-6 gap-y-2" style={{maxWidth: "36rem"}}>
                <SummaryRow label={t("start.valuations.address")}>
                    {valuation.address}
                    {valuation.referenceAddress && (
                        <div className="text-surface-400">
                            {t("start.valuations.ref")}: {valuation.referenceAddress}
                        </div>
                    )}
                </SummaryRow>
                {externalOrders.length > 0 && (
                    <SummaryRow label={t("start.valuations.externalOrderNumber")}>
                        {/* Wie im Sub-Header (OrderNumbersElement): externe Order-Nummern verlinken nach Eva+ (nur interne Lizenzen/Admins). */}
                        <span className="flex flex-wrap gap-x-3">
                            {externalOrders.map((order) => (
                                <EvaPlusOrderLink
                                    key={order.valueOrderGuid}
                                    orderGuid={order.valueOrderGuid}
                                    orderNumber={order.valueExternalOrderNumber}
                                />
                            ))}
                        </span>
                    </SummaryRow>
                )}
                {valuation.lifeCycleState && (
                    <SummaryRow label={t("start.valuations.lifeCycleState")}>
                        {/* Farbpunkt + Label wie in der Status-Spalte der Bewertungslisten (ColorDotLabel). */}
                        <ColorDotLabel
                            color={lifeCycleColors[valuation.lifeCycleState]}
                            label={t(lifeCycleStateLabelKey(valuation.lifeCycleState))}
                        />
                    </SummaryRow>
                )}
                <SummaryRow label={t("start.valuations.assignDialog.created")}>
                    {dateByUser(valuation.createdDate, valuation.createdUserName ?? valuation.createdUser)}
                </SummaryRow>
                <SummaryRow label={t("start.valuations.assignDialog.lastChanged")}>
                    {dateByUser(valuation.lastChangedDate, valuation.lastChangedUserName ?? valuation.lastChangedUser)}
                </SummaryRow>
                <SummaryRow label={t("start.valuations.id")}>
                    <span className="text-surface-500 dark:text-surface-400">{valuation.revaId}</span>
                </SummaryRow>
            </div>
            {children}
        </Dialog>
    );
}
